import { Box, Button, InputAdornment, TextField } from "@mui/material";
import { Add, Search } from "@mui/icons-material";
import { useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ConfirmDialog,
  DeleteConfirmationDialog,
  PageFilter,
} from "components";
import { DataTable, FormField } from "empower-energy-owner-components";
import {
  renderEnabled,
  renderName,
} from "components/DataTable/renderComponents";
import { getOptions } from "actions/option";
import {
  deleteVppUserRegistration,
  getVppUserRegistrationList,
} from "actions/vppUserRegistration";
import { userAction } from "actions/user";
import { BATTERY_OEMS } from "constants/select";
import { USER_TYPE } from "./utils";
import useDebounce from "hooks/useDebounce";
import { isNil } from "lodash";
import AddEditVppRegistrationModal from "./AddEditVppRegistrationModal";
import AssignSiteUserModal from "./AssignSiteUserModal";

const styles = () => {
  return {
    "& .mui-field": {
      width: "200px",
      mr: 2,
      mb: 0,
    },
    "& .search-text": {
      width: "200px",
      mr: 2,
    },
    "& .button-link": {
      p: 0,
      minWidth: 0,
      px: "2px",
    },
    "& .bar": {
      height: "16px",
      mx: "3px",
      borderRight: "1px solid #004057",
    },
  };
};

const optionLabel = (options, value) =>
  options.find((it) => it.value === value)?.label || value || "";

const VppUserRegistrations = () => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    vpp: "",
    oem: "",
    search: "",
  });
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [deleteRegistration, setDeleteRegistration] = useState(null);
  const [assignOwner, setAssignOwner] = useState(null);
  const [enableOwner, setEnableOwner] = useState(null);
  const [disableOwner, setDisableOwner] = useState(null);
  const [resendOwner, setResendOwner] = useState(null);

  const list = useSelector((state) => state.vppUserRegistration?.list);
  const loading = useSelector((state) => state.vppUserRegistration?.loading);
  const vppOptions = useSelector((state) => state.option?.vpp || []);

  const debouncedSearch = useDebounce(formData.search, 500);

  useEffect(() => {
    if (vppOptions.length === 0) {
      dispatch(getOptions());
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getVppUserRegistrationList({
        ...formData,
        search: debouncedSearch,
      }),
    );
  }, [dispatch, debouncedSearch, formData.vpp, formData.oem]);

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onClickRegister = () => {
    setSelectedRegistration("");
  };

  const refreshList = () =>
    dispatch(
      getVppUserRegistrationList({ ...formData, search: debouncedSearch }),
    );

  const onClickDelete = () => {
    dispatch(deleteVppUserRegistration(deleteRegistration.id));
    setDeleteRegistration(null);
  };

  const onClickEnable = async () => {
    await dispatch(
      userAction(
        enableOwner.owner_detail.id,
        { enabled: true },
        USER_TYPE.OWNER,
        "enable",
        refreshList,
      ),
    );
    setEnableOwner(null);
  };

  const onClickDisable = async () => {
    await dispatch(
      userAction(
        disableOwner.owner_detail.id,
        { enabled: false },
        USER_TYPE.OWNER,
        "enable",
        refreshList,
      ),
    );
    setDisableOwner(null);
  };

  const onClickResendInvite = async () => {
    await dispatch(
      userAction(
        resendOwner.owner_detail.id,
        { resend: true },
        USER_TYPE.OWNER,
        "invite",
        refreshList,
      ),
    );
    setResendOwner(null);
  };

  const renderActions = (item) => {
    const buttons = [
      { label: "Assign Sites", value: "assignSites", onClick: setAssignOwner },
      { label: "Enable", value: "enable", onClick: setEnableOwner },
      { label: "Disable", value: "disable", onClick: setDisableOwner },
      { label: "Resend Invite", value: "resendInvite", onClick: setResendOwner },
      { label: "Edit", value: "edit", onClick: setSelectedRegistration },
      { label: "Delete", value: "delete", onClick: setDeleteRegistration },
    ];
    const hasOwner = !isNil(item.owner_detail);
    const visibleButtons = buttons.filter((button) => {
      const hide =
        (button.value === "enable" && item.enabled) ||
        (button.value === "disable" && !item.enabled) ||
        (button.value === "resendInvite" &&
          item.owner_status !== "FORCE_CHANGE_PASSWORD") ||
        (["assignSites", "enable", "disable", "resendInvite"].includes(
          button.value,
        ) &&
          !hasOwner);
      return !hide;
    });
    return (
      <Box className="items-center">
        {visibleButtons.map((button, i) => (
          <Fragment key={`${item.id}${i}`}>
            <Button
              className="button-link"
              onClick={() => button.onClick(item)}
            >
              {button.label}
            </Button>
            {visibleButtons.length !== i + 1 && <Box className="bar"></Box>}
          </Fragment>
        ))}
      </Box>
    );
  };

  const constructOptions = (options) => {
    const list = options.filter((it) => it.value !== "");
    return [{ value: "", label: "Select All" }, ...list];
  };

  const columns = [
    {
      field: "email",
      headerName: "Email",
      minWidth: 180,
      flex: 1,
    },
    {
      field: "vpp",
      headerName: "VPP",
      width: 140,
      renderCell: (params) => optionLabel(vppOptions, params.value),
    },
    {
      field: "oem",
      headerName: "OEM",
      width: 120,
      renderCell: (params) => optionLabel(BATTERY_OEMS, params.value),
    },
    {
      field: "status",
      headerName: "Registration Status",
      width: 150,
    },
    {
      field: "first_name",
      headerName: "Name",
      width: 160,
      renderCell: renderName,
    },
    {
      field: "access_type_label",
      headerName: "Access Type",
      width: 110,
    },
    {
      field: "enabled",
      headerName: "Enable",
      width: 80,
      renderCell: (params) => renderEnabled(params.value),
    },
    {
      field: "owner_status",
      headerName: "Status",
      width: 200,
    },
    {
      field: "id",
      width: 360,
      headerAlign: "right",
      align: "right",
      newColumn: true,
      headerName: "Action",
      renderCell: (params) => renderActions(params.row),
    },
  ];

  return (
    <Box sx={styles}>
      <PageFilter title="VPP User Registrations"></PageFilter>

      <Box my={2} className="items-center">
        <Box className="flex-grow items-center">
          <Box className="mui-field">
            <FormField
              label="Select VPP"
              name="vpp"
              type="select"
              variant="outlined"
              value={formData.vpp}
              onChange={onChange}
              options={constructOptions(vppOptions)}
            />
          </Box>
          <Box className="mui-field">
            <FormField
              label="Select Battery System OEM"
              name="oem"
              type="select"
              variant="outlined"
              value={formData.oem}
              onChange={onChange}
              options={constructOptions(BATTERY_OEMS)}
            />
          </Box>
        </Box>
        <Box className="items-center">
          <TextField
            className="search-text"
            size="small"
            label="Search"
            name="search"
            variant="outlined"
            value={formData.search}
            onChange={onChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            onClick={onClickRegister}
            color="primary"
            variant="contained"
            startIcon={<Add />}
          >
            Register
          </Button>
        </Box>
      </Box>

      <DataTable rows={list} columns={columns} loading={loading} />

      {!isNil(selectedRegistration) && (
        <AddEditVppRegistrationModal
          selectedRegistration={selectedRegistration}
          open={true}
          onClose={() => setSelectedRegistration(null)}
        />
      )}

      {!isNil(assignOwner) && (
        <AssignSiteUserModal
          open={true}
          onClose={() => setAssignOwner(null)}
          selectedUser={assignOwner.owner_detail}
        />
      )}

      {!isNil(deleteRegistration) && (
        <DeleteConfirmationDialog
          open={true}
          onClose={() => setDeleteRegistration(null)}
          onClickDelete={onClickDelete}
          data="VPP user registration"
        />
      )}

      {!isNil(enableOwner) && (
        <ConfirmDialog
          title="Enable Owner"
          message={`Account ${enableOwner?.email} will be enabled.`}
          open={true}
          onClose={() => setEnableOwner(null)}
          onClickConfirm={onClickEnable}
        />
      )}

      {!isNil(disableOwner) && (
        <ConfirmDialog
          title="Disable Owner"
          message={`Account ${disableOwner?.email} will be disabled.`}
          open={true}
          onClose={() => setDisableOwner(null)}
          onClickConfirm={onClickDisable}
        />
      )}

      {!isNil(resendOwner) && (
        <ConfirmDialog
          title="Resend Invitation"
          message={`Resend Invitation to ${resendOwner?.email}`}
          open={true}
          onClose={() => setResendOwner(null)}
          onClickConfirm={onClickResendInvite}
        />
      )}
    </Box>
  );
};

export default VppUserRegistrations;
